import Heading from 'components/Heading';
import RecordRow from 'components/RecordRow';
import Spacing from 'components/Spacing';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import { CurrencyCode } from 'further-types/lp/vehicle';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  styled,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PreviewTable from 'components/PreviewTable';
import React from 'react';

const Container = styled(Spacing)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.borderColor?.main}`,
  paddingTop: theme.spacing(6),
}));

const StyledAccordion = styled(Accordion)({
  width: '100%',
  borderColor: '#D8D8D8',
});

const AccordionHeader = styled(AccordionSummary)(({ theme }) => ({
  padding: theme.spacing(4),
  color: theme.palette.text.darkTitle,
  fontSize: theme.fontSizes?.regular,
  fontWeight: 'bold',

  '& .MuiAccordionSummary-content': {
    margin: '0 !important',
  },
}));

const AccordionBody = styled(AccordionDetails)(({ theme }) => ({
  backgroundColor: theme.palette.background.header,
  padding: 0,
  border: 'none',
}));

type Props = {
  baseCurrency: CurrencyCode;
  equalisations: Array<{
    id: string;
    counterpartyLabel: string;
    closeDate: CalendarDay;
    numberOfNewCommitments: number;
    allocations: Array<{
      investorLabel: string;
      unpaidAllocationAmountLcy: number;
      totalFeesAmountLcy?: number;
      totalEqualisationInterestAmountLcy?: number;
      netEqualisationPayableReceivable?: number;
    }>;
    uncalledAmountLcy: number;
  }>;
};

const EqualisationsDetails: React.FC<Props> = ({
  equalisations,
  baseCurrency,
}) => {
  const columns = [
    {
      label: 'Investor',
      key: 'investorLabel',
    },
    {
      label: 'Net equalisation payable / (receivable)',
      key: 'netEqualisationPayableReceivable',
    },
    {
      label: 'Equalisation interest payable / (receivable)',
      key: 'totalEqualisationInterestAmountLcy',
    },
    {
      label: 'Management fees',
      key: 'totalFeesAmountLcy',
    },
    {
      label: 'Total payable / (receivable)',
      key: 'unpaidAllocationAmountLcy',
    },
  ];

  return (
    <Spacing>
      {equalisations.map((equalisation, index) => {
        const rows = equalisation.allocations.map((allocation) => ({
          investorLabel: allocation.investorLabel,
          netEqualisationPayableReceivable: numberToCurrencyString(
            allocation.netEqualisationPayableReceivable,
            {
              currency: baseCurrency,
              negativeInBrackets: true,
            },
          ),
          totalEqualisationInterestAmountLcy: numberToCurrencyString(
            allocation.totalEqualisationInterestAmountLcy,
            {
              currency: baseCurrency,
              negativeInBrackets: true,
            },
          ),
          totalFeesAmountLcy: numberToCurrencyString(
            allocation.totalFeesAmountLcy,
            {
              currency: baseCurrency,
            },
          ),
          unpaidAllocationAmountLcy: numberToCurrencyString(
            allocation.unpaidAllocationAmountLcy,
            {
              currency: baseCurrency,
              negativeInBrackets: true,
            },
          ),
        }));

        return (
          <Spacing key={equalisation.id}>
            <Heading variant="h2" noMargin>
              Equalisation {index + 1}: {equalisation.counterpartyLabel} (
              {equalisation.closeDate.toLabel()})
            </Heading>
            <Spacing size="sm">
              <RecordRow label="Counterparty">
                {equalisation.counterpartyLabel}
              </RecordRow>
              <RecordRow label="Close date">
                {equalisation.closeDate.toLabel()}
              </RecordRow>
              <RecordRow label="Number of new commitments">
                {equalisation.numberOfNewCommitments}
              </RecordRow>
              <RecordRow label="Uncalled amount">
                {numberToCurrencyString(equalisation.uncalledAmountLcy, {
                  currency: baseCurrency,
                })}
              </RecordRow>
            </Spacing>

            <Container>
              <StyledAccordion defaultExpanded>
                <AccordionHeader expandIcon={<ExpandMoreIcon />}>
                  Equalisation allocation
                </AccordionHeader>
                <AccordionBody>
                  <PreviewTable rows={rows} columns={columns} />
                </AccordionBody>
              </StyledAccordion>
            </Container>
          </Spacing>
        );
      })}
    </Spacing>
  );
};

export default EqualisationsDetails;
